import { useEffect, useRef } from "react";

import { usePlayground } from "../playground-store";
import { PLAYGROUND_SESSION_MODES } from "../types";

import { useAPIGetPlaygroundSessionChatMessages } from "@/queries/playground/useAPIGetPlaygroundSessionChatMessages";

/**
 * Hook that checks if there are existing chat messages in the session
 * and automatically switches to chat mode if messages exist.
 *
 * This allows users to resume chat sessions from where they left off,
 * while still allowing them to toggle back to batch mode if desired.
 */
const useInitialChatModeFromMessages = () => {
  const {
    workspace_id,
    instanceId,
    isChat,
    sessionMode,
    setSessionMode,
    isPendingSessionInitialization,
    isInitializing,
  } = usePlayground();

  const hasChecked = useRef(false);

  // Only query if we're in batch mode (not already in chat mode),
  // session has been initialized, and initialization is complete.
  // We check both isPendingSessionInitialization AND isInitializing to avoid
  // a race condition where the session mode check happens before the session
  // is fully created on the backend.
  const shouldCheck =
    isChat &&
    sessionMode === PLAYGROUND_SESSION_MODES.BATCH &&
    !isPendingSessionInitialization &&
    !isInitializing &&
    !hasChecked.current;

  const { data, isSuccess } = useAPIGetPlaygroundSessionChatMessages(
    {
      workspaceId: workspace_id,
      sessionId: instanceId,
      variableSetIndex: 0,
      page: 1,
      perPage: 1, // We only need to know if there are any messages
    },
    {
      enabled: shouldCheck,
      staleTime: Infinity, // Don't refetch this check
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (isSuccess && !hasChecked.current && shouldCheck) {
      hasChecked.current = true;

      // If there are messages, switch to chat mode
      if (data && (data.total > 0 || data.messages?.length)) {
        setSessionMode(PLAYGROUND_SESSION_MODES.CHAT);
      }
    }
  }, [isSuccess, data, shouldCheck, setSessionMode]);

  // When the session initialises already in chat mode the `shouldCheck`
  // condition is never true, so `hasChecked` stays false.  Without this
  // guard, a later manual "Switch to Template" would flip sessionMode to
  // BATCH, satisfy `shouldCheck`, re-detect the existing chat messages
  // and immediately revert back to CHAT.
  useEffect(() => {
    if (
      !isPendingSessionInitialization &&
      !isInitializing &&
      sessionMode === PLAYGROUND_SESSION_MODES.CHAT
    ) {
      hasChecked.current = true;
    }
  }, [isPendingSessionInitialization, isInitializing, sessionMode]);

  // Reset the check flag when session changes
  useEffect(() => {
    hasChecked.current = false;
  }, [instanceId]);
};

export default useInitialChatModeFromMessages;
